import { AnswerOT, SheetOT, SheetAnswerTuple } from "../Sheet";

/**
 * Checks if a sheet should have a blue border based on the specified rules
 * @param sheet The sheet to check
 * @returns True if the sheet should have a blue border, false otherwise
 */
export const shouldHaveBlueBorder = (sheet: SheetOT): boolean => {
  // If the sheet is completed (has FechaRevisionFinal), don't show blue border
  if (sheet.FechaRevisionFinal) {
    return false;
  }

  // If the checklist is completed ("Aprobado") or rejected ("Rechazado"), return false
  if (sheet.Estado === "Aprobado" || sheet.Estado === "Rechazado") {
    return false;
  }

  // Check each reviewer individually
  return sheet.Revisores.some(revisor => {
    // Get all questions for this reviewer
    const questions = revisor.Categorias.flatMap(category => category.Preguntas);

    // Filter out questions with "Bloqueado" status
    const activeQuestions = questions.filter(question =>
      !question.Respuestas.some(resp => resp?.Estado === "Bloqueado")
    );

    if (activeQuestions.length === 0) return false;

    // Count questions by status
    const blockedCount = questions.filter(q =>
      q.Respuestas.some(resp => resp?.Estado === "Bloqueado")
    ).length;

    const rejectedCount = activeQuestions.filter(q =>
      q.Respuestas.some(resp => resp?.Estado === "Rechazado")
    ).length;

    const pendingCount = activeQuestions.filter(q =>
      q.Respuestas.some(resp => resp?.Estado === "Pendiente")
    ).length;

    const approvedCount = activeQuestions.filter(q =>
      q.Respuestas.some(resp => resp?.Estado === "Aprobado")
    ).length;

    // Rule 1: Combination of "Bloqueado" and "Rechazado"
    if (blockedCount > 0 && rejectedCount > 0) {
      return true;
    }

    // Rule 2: Combination of "Pendiente" and "Aprobado"
    if (pendingCount > 0 && approvedCount > 0) {
      return true;
    }

    // Rule 3: All "Aprobado" without any "Pendiente"
    if (approvedCount === activeQuestions.length && pendingCount === 0) {
      return true;
    }

    // Rule 4: Combination of "Rechazado" and "Pendiente"
    if (rejectedCount > 0 && pendingCount > 0) {
      return true;
    }

    // Rule 5: All "Aprobado" except one "Pendiente"
    if (approvedCount === activeQuestions.length - 1 && pendingCount === 1) {
      return true;
    }

    // Rule 6: Only one "Pendiente" and no other responses
    if (pendingCount === 1 && approvedCount === 0 && rejectedCount === 0) {
      return true;
    }

    // Rule 7: At least one "Aprobado" and at least one null response (sin responder)
    // Note: We already handle the case of "Pendiente" responses in Rule 2
    const nullResponseCount = activeQuestions.filter(q =>
      q.Respuestas.some(resp => resp === null)
    ).length;

    if (approvedCount > 0 && nullResponseCount > 0) {
      return true;
    }

    // Rule 8: If there's a "Rechazado" (N) response and there are other pending responses (?)
    // This ensures blue border remains when there's an N response with unanswered questions
    if (rejectedCount > 0 && nullResponseCount > 0) {
      return true;
    }

    return false;
  });
};

// Checks if a sheet has X/P combination or unanswered questions (?)
export const hasXPOrUnansweredQuestions = (sheet: SheetOT): boolean => {
  // Get all questions from all reviewers
  const allQuestions = sheet.Revisores.flatMap(revisor =>
    revisor.Categorias.flatMap(category => category.Preguntas)
  );

  // Check if any question has X/P combination or is unanswered
  return allQuestions.some(question => {
    const [first, second] = question.Respuestas;

    // X/P combination: one response is "Rechazado" (X) and another is "Pendiente" (P)
    const hasXPCombination =
      (first?.Estado === "Rechazado" && second?.Estado === "Pendiente") ||
      (first?.Estado === "Pendiente" && second?.Estado === "Rechazado");

    // Unanswered question: response is null or "Pendiente"
    const hasUnansweredQuestion =
      first === null ||
      first?.Estado === "Pendiente" ||
      second === null ||
      second?.Estado === "Pendiente";

    return hasXPCombination || hasUnansweredQuestion;
  });
};

// Checks if a sheet has X/P combination (without checking for unanswered questions)
export const hasXPCombination = (sheet: SheetOT): boolean => {
  // If sheet is null or undefined, return false
  if (!sheet) return false;

  // Get all questions from all reviewers
  const allQuestions = sheet.Revisores.flatMap(revisor =>
    revisor.Categorias.flatMap(category => category.Preguntas)
  );

  // Check if any question has X/P combination
  return allQuestions.some(question => {
    const [first, second] = question.Respuestas;

    // X/P combination: one response is "Rechazado" (X) and another is "Pendiente" (P)
    return (first?.Estado === "Rechazado" && second?.Estado === "Pendiente") ||
           (first?.Estado === "Pendiente" && second?.Estado === "Rechazado");
  });
};

// Checks if any sheet in a place has X/P combinations
export const hasBlueYellowResponses = (sheets: SheetOT[]): boolean => {
  // If there are no sheets at all, return false
  if (!sheets || sheets.length === 0) {
    return false;
  }

  // Return true if any sheet has X/P combinations
  return sheets.some(sheet => hasXPCombination(sheet));
};

export const hasYellowResponses = (sheets: SheetOT[]): boolean => {
  // If there are no sheets at all, return false
  if (!sheets || sheets.length === 0) {
    return false;
  }
  // Only return true if there is at least one sheet without FechaRevisionFinal (not completed)
  return sheets.some(sheet => !sheet.FechaRevisionFinal);
};

export const twoOrMoreAreWrong = (
	sheet: Pick<SheetOT, "Revisores">
): boolean => {
	const responses = sheet.Revisores.flatMap((r) =>
		r.Categorias.flatMap((c) => c.Preguntas)
	);

	return responses.some((res) => {
		const [first, second] = res.Respuestas;
		const firstIsWrong = first?.Estado === "Rechazado";
		const secondIsWrong = second?.Estado === "Rechazado";
		return firstIsWrong && secondIsWrong;
	});
};

export const getDefaultComment = ({
	Estado,
}: Pick<AnswerOT, "Estado">) => {
	switch (Estado) {
		case "Aprobado":
			return "Conforme";
		case "Rechazado":
			return "N/A";
		case "No Aplica":
			return "No se considera";
		case "Pendiente":
			return "Por revisar";
		case "Bloqueado":
			return "No revisa";
	}
};

export const getReviewerStatusColor = (
	sheet: SheetOT,
	reviewer: number
) => {
	const { Firma, Categorias } = sheet.Revisores[reviewer];
	const responses = Categorias.flatMap(
		(c) => c.Preguntas
	).flatMap((c) => c.Respuestas);

	if (Firma) return "lightgreen";
	if (responses.some((r) => r?.Estado === "Bloqueado"))
		return "gold";
	return "white";
};
